import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { validateInviteToken, acceptInvite } from "../../services/subAccountService";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import Loader from "../../components/common/Loader";
import LanguageSwitcher from "../../components/common/LanguageSwitcher";
import { APP_NAME } from "../../utils/constants";
import { color, radius, shadow } from "../../styles/theme";

// Same rule as Login's forgot-password reset screen (mobile's ResetPassword screen).
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{8,})/;

const getErrorMessage = (err, fallback) => {
  const respData = err.response?.data;
  return (typeof respData === "string" ? respData : null) || respData?.Message || respData?.message || fallback;
};

/**
 * Public (unauthenticated) Sub-Account invite-acceptance page — Promoter
 * Panel spec ch.11.3: recipient sees which Business Account invited them,
 * confirms required rules/data-protection notices, and sets their OWN
 * password (never transmitted by the Business Account). No route precedent
 * existed for a second public page — built fresh alongside Login's pattern.
 */
const AcceptInvite = () => {
  const { t } = useTranslation(["subAccounts", "common"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("validating"); // validating | valid | invalid | done
  const [ownerBusinessName, setOwnerBusinessName] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmedRules, setConfirmedRules] = useState(false);
  const [confirmedDataProtection, setConfirmedDataProtection] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    validateInviteToken(token)
      .then((res) => {
        const result = res.data?.Result;
        if (result?.IsValid) {
          setOwnerBusinessName(result.OwnerBusinessName || "");
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!confirmedRules || !confirmedDataProtection) {
      toast.error(t("acceptInvite.errors.mustConfirmBoth"));
      return;
    }
    if (!PASSWORD_REGEX.test(password)) {
      toast.error(t("acceptInvite.errors.passwordRequirements"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("acceptInvite.errors.passwordsDontMatch"));
      return;
    }

    setSubmitting(true);
    try {
      await acceptInvite({
        Token: token,
        Password: password,
        ConfirmedRules: confirmedRules,
        ConfirmedDataProtection: confirmedDataProtection,
      });
      setStatus("done");
    } catch (err) {
      toast.error(getErrorMessage(err, t("acceptInvite.errors.activateFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.bgImage} />
      <div style={s.bgScrim} />
      <div style={s.langSwitcherWrap}>
        <LanguageSwitcher />
      </div>
      <div className="modal-pop" style={s.card}>
        <img src="/PS_favicon.png" alt="" style={s.logo} />

        {status === "validating" && (
          <>
            <h2 style={s.title}>{APP_NAME}</h2>
            <p style={s.subtitle}>{t("acceptInvite.validating")}</p>
            <Loader />
          </>
        )}

        {status === "invalid" && (
          <>
            <div style={{ ...s.iconWrap, color: color.danger500 }}><XCircle size={40} strokeWidth={1.75} /></div>
            <h2 style={s.title}>{t("acceptInvite.invalidTitle")}</h2>
            <p style={s.subtitle}>{t("acceptInvite.invalidMessage")}</p>
            <Button onClick={() => navigate("/login")} style={{ width: "100%", marginTop: 10 }}>
              {t("acceptInvite.backToLogin")}
            </Button>
          </>
        )}

        {status === "done" && (
          <>
            <div style={{ ...s.iconWrap, color: color.success500 }}><CheckCircle2 size={40} strokeWidth={1.75} /></div>
            <h2 style={s.title}>{t("acceptInvite.successTitle")}</h2>
            <p style={s.subtitle}>{t("acceptInvite.successMessage")}</p>
            <Button onClick={() => navigate("/login")} style={{ width: "100%", marginTop: 10 }}>
              {t("acceptInvite.goToLogin")}
            </Button>
          </>
        )}

        {status === "valid" && (
          <>
            <h2 style={s.title}>{t("acceptInvite.title", { business: ownerBusinessName })}</h2>
            <p style={s.subtitle}>{t("acceptInvite.subtitle")}</p>

            <form onSubmit={handleSubmit}>
              <div style={{ position: "relative" }}>
                <Input
                  label={t("acceptInvite.passwordLabel")}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} style={s.eyeBtn} tabIndex={-1} aria-label="toggle password visibility">
                  {showPassword ? <EyeOff size={17} strokeWidth={2} /> : <Eye size={17} strokeWidth={2} />}
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <Input
                  label={t("acceptInvite.confirmPasswordLabel")}
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} style={s.eyeBtn} tabIndex={-1} aria-label="toggle password visibility">
                  {showConfirmPassword ? <EyeOff size={17} strokeWidth={2} /> : <Eye size={17} strokeWidth={2} />}
                </button>
              </div>
              <p style={s.hint}>{t("acceptInvite.passwordHint")}</p>

              <label style={s.checkboxRow}>
                <input type="checkbox" checked={confirmedRules} onChange={(e) => setConfirmedRules(e.target.checked)} />
                <span>{t("acceptInvite.confirmRules")}</span>
              </label>
              <label style={s.checkboxRow}>
                <input type="checkbox" checked={confirmedDataProtection} onChange={(e) => setConfirmedDataProtection(e.target.checked)} />
                <span>{t("acceptInvite.confirmDataProtection")}</span>
              </label>

              <Button type="submit" disabled={submitting} style={{ width: "100%", marginTop: 14 }}>
                {submitting ? t("acceptInvite.activating") : t("acceptInvite.activate")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" },
  bgImage: {
    position: "absolute", inset: "-10px", backgroundImage: "url(/PS.png)", backgroundSize: "cover",
    backgroundPosition: "center", filter: "blur(3px)", zIndex: 0,
  },
  bgScrim: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(35,8,10,0.12) 0%, rgba(25,6,10,0.22) 55%, rgba(20,5,8,0.4) 100%)", zIndex: 0 },
  langSwitcherWrap: { position: "absolute", top: 20, right: 24, zIndex: 2 },
  card: {
    position: "relative", zIndex: 1, background: "rgba(255,255,255,0.97)", padding: "40px 40px 36px",
    borderRadius: radius.xl, boxShadow: shadow.lg, width: "100%", maxWidth: 420, backdropFilter: "blur(10px)",
  },
  logo: { width: 44, height: 44, borderRadius: radius.sm, display: "block", margin: "0 auto 14px" },
  title: { margin: "0 0 4px", fontSize: 20, fontWeight: 800, color: color.textPrimary, textAlign: "center", letterSpacing: -0.3 },
  subtitle: { margin: "0 0 22px", color: color.textMuted, textAlign: "center", fontSize: 13.5, lineHeight: 1.5 },
  iconWrap: { display: "flex", justifyContent: "center", marginBottom: 10 },
  eyeBtn: {
    position: "absolute", right: 10, top: 33, background: "none", border: "none", cursor: "pointer",
    padding: "4px 6px", lineHeight: 1, color: color.gray500, display: "flex",
  },
  hint: { fontSize: 11.5, color: color.textMuted, margin: "-8px 0 14px", lineHeight: 1.5 },
  checkboxRow: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: color.gray700, margin: "10px 0", cursor: "pointer", lineHeight: 1.4 },
};

export default AcceptInvite;
